import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain, BookOpen, GraduationCap, FileText, Building2, Network,
  ArrowRight, ChevronRight, ScrollText, Sparkles, Telescope, Globe, Award, Eye
} from "lucide-react";
import ExecutiveHero from "@/components/ExecutiveHero";
import { OFFICIAL_SEAL } from "@/lib/brand";

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Cinematic editorial hero — diverse executive cohort engaged in strategic
// workforce intelligence collaboration. Communicates Workforce Intelligence,
// Executive Education, and Human-AI institutional capability within the first
// three seconds (per Visual Experience Upgrade Directive v1.0).
const HOME_HERO = "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=2400&q=85";

const PILLARS = [
  { id: "wait-theory",  to: "/wait-theory",  title: "WAIT Theory",       desc: "The doctrinal framework for workforce alignment in the Human-AI era.", icon: Brain,         accent: "#1E3A8A" },
  { id: "scfr",         to: "/scfr-journal", title: "SCFR-Journal",      desc: "Peer research, doctrine, and credential-fatigue scholarship.",         icon: ScrollText,    accent: "#991B1B" },
  { id: "programs",     to: "/programs",     title: "Executive Programs",desc: "Masters, certificates, courses, and institutional licensing.",         icon: GraduationCap, accent: "#1E3A8A" },
  { id: "observatory",  to: "/observatories/global", title: "Observatory", desc: "Global workforce intelligence index, dashboards, and instrumentation.", icon: Telescope,   accent: "#C8A157" },
  { id: "juswork",      to: "/juswork",       title: "JusWork",           desc: "Workforce justice, opportunity intelligence, and mobility.",          icon: Building2,    accent: "#991B1B" },
  { id: "lms-lab",      to: "/ecosystem/zodiac-lms-lab", title: "Zodiac LMS-Lab", desc: "Premium hybrid-intelligence learning environments.",        icon: Sparkles,     accent: "#1E3A8A" },
  { id: "partnership",  to: "/ecosystem/partnership", title: "Partnership & Global Network", desc: "Institutional partnerships and global doctrine adoption.", icon: Globe, accent: "#C8A157" },
  { id: "about",        to: "/about",         title: "About the Institute", desc: "Mission, vision, and the founder's authoritative leadership.",    icon: Network,      accent: "#0F1E47" },
];

const STATS = [
  { value: "5",  label: "Intelligence Indices" },
  { value: "10", label: "Industry Sectors" },
  { value: "50+", label: "Research Outputs" },
  { value: "1",  label: "Constitutional Doctrine" },
];

export default function Home() {
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/scfr/publications/featured`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFeatured(d?.publications?.[0] || null))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen zodiac-page" data-testid="home-page">
      {/* Cinematic Editorial Hero */}
      <ExecutiveHero
        eyebrow="Workforce Intelligence Institute"
        title={<>Beyond Training & Credential —</>}
        accentTitle="Toward measurable workforce intelligence."
        subtitle="A premium institutional ecosystem pioneering the alignment of artificial intelligence with human workforce capability. Research, doctrine, programs, and instrumentation — engineered for executive transformation."
        image={HOME_HERO}
        actions={[
          { label: "Explore Programs", href: "/programs", testId: "cta-explore-programs" },
          { label: "Read SCFR-Journal", href: "/scfr-journal", variant: "ghost", testId: "cta-scfr-journal" },
        ]}
      />

      {/* Constitutional Authority Quick-View */}
      {featured && (
        <section className="bg-[#FBFAF7] border-y border-[#E5E7EB]" data-testid="home-featured-research">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-[1fr_360px] gap-10 items-center">
            <div>
              <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
                <span className="w-8 h-px bg-[#991B1B]" />
                Featured Research
              </div>
              <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold mb-4 leading-tight">
                {featured.title}
              </h2>
              {featured.subtitle && (
                <p className="text-lg text-[#475569] mb-4">{featured.subtitle}</p>
              )}
              <p className="text-[#475569] leading-relaxed border-l-2 border-[#C8A157] pl-4 mb-6 max-w-3xl">
                {(featured.abstract || "").slice(0, 320)}…
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/scfr/${featured.slug}`}>
                  <button className="h-11 px-5 rounded-md bg-[#991B1B] text-white hover:bg-[#7F1D1D] font-semibold inline-flex items-center gap-2">
                    Read the Paper <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                {featured.pdf_url && (
                  <a href={featured.pdf_url} download>
                    <button className="h-11 px-5 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-white inline-flex items-center gap-2">
                      Download PDF
                    </button>
                  </a>
                )}
              </div>
            </div>
            {featured.featured_image_url && (
              <Link to={`/scfr/${featured.slug}`} className="block group">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_18px_60px_rgba(15,30,71,0.18)] ring-1 ring-[#0F1E47]/5">
                  <img
                    src={featured.featured_image_url}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Pillar Grid — Institutional Architecture */}
      <section className="bg-white py-20" data-testid="navigation-system">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[#1E3A8A]" />
              Institutional Architecture
            </div>
            <h2 className="zodiac-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Eight pillars. <span className="italic text-[#991B1B] font-normal">One ecosystem.</span>
            </h2>
            <p className="mt-4 text-[#475569] max-w-2xl">
              Navigate the integrated platform — from foundational theory and peer-reviewed
              research to executive programs, observatory dashboards, and global partnerships.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                viewport={{ once: true }}
              >
                <Link
                  to={p.to}
                  className="zodiac-hairline-card group block h-full p-6"
                  data-testid={`nav-box-${p.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-11 h-11 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${p.accent}12` }}
                    >
                      <p.icon className="w-5 h-5" style={{ color: p.accent }} />
                    </div>
                    <ChevronRight className="ml-auto w-4 h-4 text-[#9CA3AF] group-hover:text-[#1E3A8A] group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="zodiac-heading text-xl font-bold text-[#0F1E47] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{p.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-[#0F1E47] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center lg:text-left border-l border-white/10 pl-5 first:border-l-0 first:pl-0">
              <div className="zodiac-heading text-4xl lg:text-5xl font-bold text-[#C8A157] tabular-nums">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Doctrine + Programs Dual CTA */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-[#FBFAF7] border border-[#E5E7EB] p-8 lg:p-10 flex flex-col">
            <div className="w-12 h-12 rounded-lg bg-[#991B1B]/10 flex items-center justify-center mb-5">
              <BookOpen className="w-6 h-6 text-[#991B1B]" />
            </div>
            <h3 className="zodiac-heading text-2xl font-bold text-[#0F1E47] mb-3">Read the Doctrine</h3>
            <p className="text-[#475569] mb-6 leading-relaxed">
              Engage with WAIT Theory, the constitutional framework defining
              workforce intelligence in the Human-AI era.
            </p>
            <Link to="/wait-theory" className="mt-auto">
              <button className="h-11 px-5 rounded-md bg-[#0F1E47] text-white hover:bg-[#1E3A8A] font-semibold inline-flex items-center gap-2">
                Open WAIT Theory <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>

          <div className="rounded-2xl bg-[#0F1E47] text-white p-8 lg:p-10 flex flex-col relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-[#C8A157]/15 blur-3xl" />
            <div className="relative">
              <div className="w-12 h-12 rounded-lg bg-[#C8A157]/15 flex items-center justify-center mb-5">
                <Award className="w-6 h-6 text-[#C8A157]" />
              </div>
              <h3 className="zodiac-heading text-2xl font-bold mb-3">Apply to Zodiac</h3>
              <p className="text-white/70 mb-6 leading-relaxed">
                Executive masters, certificates, and licensing — engineered for
                institutional leaders shaping the workforce of the next decade.
              </p>
              <Link to="/programs" className="mt-auto">
                <button className="h-11 px-5 rounded-md bg-[#C8A157] text-[#0F1E47] hover:bg-[#E5C275] font-semibold inline-flex items-center gap-2">
                  Explore Programs <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FBFAF7] border-t border-[#E5E7EB] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img
              src={OFFICIAL_SEAL}
              alt="Zodiac Workforce Intelligence Institute"
              className="h-16 w-auto drop-shadow-[0_4px_10px_rgba(15,30,71,0.15)]"
            />
            <div>
              <div className="zodiac-heading text-[#0F1E47] font-bold text-base leading-tight">ZODIAC</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#991B1B] font-semibold">
                Workforce Intelligence Institute<sup className="text-[7px] ml-0.5 align-super">™</sup>
              </div>
              <div className="text-[11px] text-[#9CA3AF] mt-1">© 2026 — All rights reserved</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#475569] justify-center md:justify-end">
            <Link to="/about" className="hover:text-[#0F1E47]">About</Link>
            <Link to="/founder" className="hover:text-[#0F1E47]">Founder</Link>
            <Link to="/research" className="hover:text-[#0F1E47]">Research</Link>
            <Link to="/programs" className="hover:text-[#0F1E47]">Programs</Link>
            <Link to="/scfr-journal" className="hover:text-[#0F1E47]">SCFR-Journal</Link>
            <Link to="/press" className="hover:text-[#0F1E47]">Press</Link>
            <Link to="/media-kit" className="hover:text-[#0F1E47]">Media Kit</Link>
            <Link to="/contact" className="hover:text-[#0F1E47]">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
